import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { DecimalOnlyDirective } from '../../core/directives';
import { AppDataService } from '../../core/services/app-data.service';
import { InventoryService } from '../../core/services/inventory.service';
import { ExportService } from '../../core/services/export.service';
import { SettingsService } from '../../core/services/settings.service';
import { AssetRegisterEntry } from '../../core/models/inventory.models';
import { confirmAction } from '../../core/utils/confirm.util';
import { currentTimestamp, formatCurrency } from '../../core/utils/format.util';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-asset-register',
  imports: [FormsModule, DecimalOnlyDirective],
  standalone: true,
  templateUrl: './asset-register.component.html',
  styleUrl: './asset-register.component.css',
})
export class AssetRegisterComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly appData = inject(AppDataService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly categories = this.settings.allCategories;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;

  readonly search = signal('');
  readonly categoryFilter = signal('all');
  readonly statusFilter = signal('all');
  readonly locationFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 12;

  readonly showRegisterModal = signal(false);
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly editingId = signal<string | null>(null);

  readonly isEditing = computed(() => this.editingId() !== null);

  readonly newAsset = signal({
    assetTag: '',
    name: '',
    category: '',
    location: '',
    purchaseDate: '',
    purchaseValue: 0,
    condition: 'Good',
    status: 'active' as AssetRegisterEntry['status'],
  });

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.appData.refreshAll();
    }
  }

  readonly stats = computed(() => {
    const assets = this.inventory.allAssets();
    return {
      total: assets.length,
      active: assets.filter((a) => a.status === 'active').length,
      retired: assets.filter((a) => a.status === 'retired').length,
      disposed: assets.filter((a) => a.status === 'disposed').length,
    };
  });

  readonly filteredAssets = computed(() => {
    const search = this.search().trim().toLowerCase();
    const category = this.categoryFilter();
    const status = this.statusFilter();
    const location = this.locationFilter();

    return this.inventory.allAssets().filter((asset) => {
      const matchesSearch =
        !search ||
        asset.assetTag.toLowerCase().includes(search) ||
        asset.name.toLowerCase().includes(search) ||
        asset.category.toLowerCase().includes(search);
      const matchesCategory = category === 'all' || asset.category === category;
      const matchesStatus = status === 'all' || asset.status === status;
      const matchesLocation = location === 'all' || asset.location === location;
      return (
        matchesSearch && matchesCategory && matchesStatus && matchesLocation
      );
    });
  });

  readonly paginatedAssets = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredAssets().slice(start, start + this.pageSize);
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredAssets().length / this.pageSize)),
  );

  readonly paginationStart = computed(() =>
    this.filteredAssets().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );

  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredAssets().length),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  updateNewAsset(
    field: keyof ReturnType<typeof this.newAsset>,
    value: string | number,
  ): void {
    this.newAsset.update((asset) => ({ ...asset, [field]: value }));
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.page.set(page);
    }
  }

  openRegisterModal(): void {
    this.editingId.set(null);
    this.newAsset.set({
      assetTag: '',
      name: '',
      category: '',
      location: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchaseValue: 0,
      condition: 'Good',
      status: 'active',
    });
    this.showRegisterModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  openEditModal(asset: AssetRegisterEntry): void {
    this.editingId.set(asset.id);
    this.newAsset.set({
      assetTag: asset.assetTag,
      name: asset.name,
      category: asset.category,
      location: asset.location,
      purchaseDate: asset.purchaseDate,
      purchaseValue: asset.purchaseValue,
      condition: asset.condition,
      status: asset.status,
    });
    this.showRegisterModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeRegisterModal(): void {
    this.showRegisterModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeRegisterModal();
    }
  }

  registerAsset(): void {
    const asset = this.newAsset();
    if (!asset.assetTag || !asset.name || !asset.category || !asset.location) {
      return;
    }

    const payload = {
      assetTag: asset.assetTag,
      name: asset.name,
      category: asset.category,
      location: asset.location,
      purchaseDate: asset.purchaseDate || new Date().toISOString().slice(0, 10),
      purchaseValue: asset.purchaseValue,
      condition: asset.condition,
      status: asset.status,
    };

    if (this.editingId()) {
      if (!environment.useMockData) {
        this.toastMessage.set('Asset update is not available via the API yet.');
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        return;
      }
      this.inventory.updateAsset(this.editingId()!, payload);
      this.toastMessage.set('Asset updated successfully');
    } else {
      if (!environment.useMockData) {
        this.toastMessage.set(
          'Register equipment assets via Item Master with “Rentable” enabled, then refresh.',
        );
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 5000);
        return;
      }
      this.inventory.addAsset(payload);
      this.toastMessage.set('Asset registered successfully');
    }

    this.closeRegisterModal();
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  deleteAsset(asset: AssetRegisterEntry): void {
    if (
      !confirmAction(
        `Delete asset "${asset.name}" (${asset.assetTag})? This cannot be undone.`,
      )
    ) {
      return;
    }

    if (!environment.useMockData) {
      this.toastMessage.set('Asset delete is not available via the API yet.');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    this.inventory.deleteAsset(asset.id);
    this.toastMessage.set('Asset deleted successfully');
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  statusLabel(status: AssetRegisterEntry['status']): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  exportData(): void {
    const rows = this.filteredAssets().map((asset) => ({
      assetTag: asset.assetTag,
      name: asset.name,
      category: asset.category,
      location: asset.location,
      purchaseDate: asset.purchaseDate,
      purchaseValue: asset.purchaseValue,
      condition: asset.condition,
      status: asset.status,
    }));
    this.exporter.downloadCsv(
      `asset-register-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }
}
